import { useMemo, useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useContact, useUpdateContact } from './hooks/useContacts';
import { useFolderTemplates } from '@/features/settings/hooks/useFolderTemplates';
import {
  useClientFolderTree,
  useApplyFolderTemplate,
  type ClientFolderNode,
} from '@/features/documents/hooks/useClientFolders';

/**
 * ContactFolderStructure — shown at the top of a contact's Documents tab.
 *
 * Lets staff associate a folder template with the contact (from Setup) and
 * see the materialized folder tree. Applying a template creates the folders
 * under the client so uploads (here, in jobs, and on the portal) stay
 * organized under the same structure.
 */
export function ContactFolderStructure({ contactId }: { contactId: string }) {
  const { can } = usePermissions();
  const { toast } = useToast();

  const { data: contact } = useContact(contactId);
  const { data: templates } = useFolderTemplates();
  const { data: folderTree, isLoading: treeLoading } = useClientFolderTree(contactId);

  const updateContact = useUpdateContact(contactId);
  const applyTemplate = useApplyFolderTemplate();

  const canManage = can(PERMISSIONS.DOCUMENTS_MANAGE_FOLDERS);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    contact?.folderTemplateId ?? '',
  );

  // Keep the local select in sync once the contact loads.
  const currentTemplateId = contact?.folderTemplateId ?? '';
  const effectiveSelected = selectedTemplateId || currentTemplateId;

  const templateOptions = useMemo(
    () => [
      { value: '', label: 'No template' },
      ...(templates ?? []).map((t) => ({
        value: t.id,
        label: t.isDefault ? `${t.name} (default)` : t.name,
      })),
    ],
    [templates],
  );

  const handleSaveAndApply = async () => {
    const templateId = effectiveSelected || null;
    try {
      // Persist the assignment on the contact (the backend also auto-applies,
      // but we apply explicitly too so the tree refreshes deterministically).
      await updateContact.mutateAsync({
        firstName: contact?.firstName ?? '',
        lastName: contact?.lastName ?? '',
        folderTemplateId: templateId,
      });

      const result = await applyTemplate.mutateAsync({ clientId: contactId, templateId });
      toast({
        variant: 'success',
        title: templateId ? 'Folder template applied' : 'Folder assignment saved',
        description:
          result.created > 0
            ? `${result.created} folder${result.created === 1 ? '' : 's'} created.`
            : 'Folders are already up to date.',
      });
    } catch {
      toast({ variant: 'error', title: 'Failed to apply folder template' });
    }
  };

  const isBusy = updateContact.isPending || applyTemplate.isPending;
  const hasFolders = (folderTree?.length ?? 0) > 0;

  return (
    <Card>
      <CardHeader
        title="Folder Structure"
        description="Organize this client's documents using a reusable folder template defined in Settings."
      />
      <CardContent>
        {canManage && (
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <Select
                label="Folder template"
                options={templateOptions}
                value={effectiveSelected}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              />
            </div>
            <Button onClick={handleSaveAndApply} loading={isBusy}>
              {effectiveSelected ? 'Apply template' : 'Save'}
            </Button>
            {hasFolders && (
              <Button
                variant="outline"
                onClick={() =>
                  applyTemplate.mutate(
                    { clientId: contactId },
                    {
                      onSuccess: (r) =>
                        toast({
                          variant: 'success',
                          title: 'Folders re-synced',
                          description:
                            r.created > 0
                              ? `${r.created} missing folder${r.created === 1 ? '' : 's'} added.`
                              : 'No changes needed.',
                        }),
                      onError: () => toast({ variant: 'error', title: 'Re-sync failed' }),
                    },
                  )
                }
                loading={applyTemplate.isPending}
                title="Add any folders missing from the current template"
              >
                Re-sync
              </Button>
            )}
          </div>
        )}

        {treeLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading folders...</p>
        ) : hasFolders ? (
          <div className="rounded-lg border border-border p-3 dark:border-border-dark">
            <FolderTreeView nodes={folderTree ?? []} depth={0} />
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No folders yet.{' '}
            {canManage ? 'Choose a template and apply it to create the structure.' : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Read-only folder tree ──────────────────────────────────────────────────

function FolderTreeView({ nodes, depth }: { nodes: ClientFolderNode[]; depth: number }) {
  return (
    <ul className="space-y-1" role="list">
      {nodes.map((node) => (
        <li key={node.id}>
          <div
            className="flex items-center gap-2 py-1 text-sm text-gray-700 dark:text-gray-300"
            style={{ paddingLeft: depth * 16 }}
          >
            <span aria-hidden="true">📁</span>
            <span>{node.name}</span>
          </div>
          {node.children.length > 0 && <FolderTreeView nodes={node.children} depth={depth + 1} />}
        </li>
      ))}
    </ul>
  );
}
